import { Button } from '@onestep/coreui/Button';
import axios from 'axios';
import { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as OneStepLogo } from '@/assets/icons/Login/logo.svg';
import useLoggedUser from '@/hooks/useLoggedUser';
import useMixpanel from '@/hooks/useMixpanel';
import { DEFAULT_NS } from '@/i18n/types';

export const NoWorkspaceAccess = () => {
  const { t } = useTranslation(DEFAULT_NS, { keyPrefix: 'noWorkspaceAccess' });
  const { myProfile, organization, logout } = useLoggedUser();
  const { track } = useMixpanel();
  const hasTracked = useRef(false);

  useEffect(() => {
    if (myProfile?.uuid && !hasTracked.current) {
      hasTracked.current = true;
      track('viewed_no_workspace_access_page', {
        user_uuid: myProfile.uuid,
      });
    }
  }, [myProfile?.uuid, track]);

  const handleLogout = () => {
    track('clicked_no_workspace_access_logout');
    logout(axios.CancelToken.source());
  };

  const orgName = organization?.displayName || organization?.name;

  return (
    <div
      className="w-full h-screen flex items-center justify-center bg-neutral-m5 p-6 overflow-auto"
      data-component="NoWorkspaceAccess"
    >
      <div className="flex flex-col items-center text-center gap-6 w-full max-w-[620px] p-10 rounded-4 bg-white shadow-medium border border-neutral-m2">
        <div className="flex items-center justify-center gap-2">
          <OneStepLogo />
          <div className="text-neutral-p3 font-bold text-2.5xl">OneStep</div>
        </div>

        <div className="flex flex-col items-center gap-4">
          {orgName && (
            <span className="text-neutral-p1 text-sm font-semibold uppercase tracking-wide">
              {t('orgLabel', { orgName })}
            </span>
          )}
          <h1 className="text-neutral-p3 text-3xl font-bold text-balance">
            {t('title')}
          </h1>
          <p className="text-neutral-p2 text-base text-balance max-w-[460px]">
            {t('body')}
          </p>
          <p className="text-neutral-p1 text-sm text-balance max-w-[460px]">
            <Trans
              i18nKey="nudge"
              t={t}
              components={{ bold: <span className="text-neutral-p2 font-semibold" /> }}
            />
          </p>
        </div>

        <div className="flex justify-center w-full border-t border-neutral-m2 pt-6">
          <Button
            variant="secondary"
            onClick={handleLogout}
            label={t('logout')}
            data-testid="no-workspace-access-logout"
          />
        </div>
      </div>
    </div>
  );
};
